import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole, { IUseRole } from '~/store/user/hooks/useRole';
import {
  clientMapper,
  ITeamMemberMapped,
  sortByName,
  teamMemberMapper,
} from '~/utils/clientAllocation';
import extractFullName from '~/utils/text/extractFullName';
import { useApiRequest } from '~/hooks/api';
import { IAllocationClientsByGroup } from '~/services/api/clientAllocation/types';
import api from '~/services/api';
import { AllocationMode } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/constants/allocationMode';
import { IOption } from '~/types';

type IAddParams = {
  actTeamId: string;
};

type AddClientAllocationContextValue = {
  isLoading: boolean;
  teamMembers: ITeamMemberMapped[];
  actTeamId: string;
  clinicId: string;
  clients: IOption[];
  locationGroups: IAllocationClientsByGroup[];
  allocationMode: AllocationMode;
  setAllocationMode: Dispatch<SetStateAction<AllocationMode>>;
  currentUserRole: IUseRole;
};

const AddClientAllocationContext = createContext<AddClientAllocationContextValue | null>(null);

interface AddClientAllocationProviderProps {
  children: ReactNode;
}

export const AddClientAllocationProvider = ({
  children,
}: AddClientAllocationProviderProps): ReactElement => {
  const { actTeamId } = useParams<IAddParams>();

  const {
    clinic: userClinic,
    id: userId,
    photo,
    ...user
  } = useStoreState(state => state.user.current);
  const locationGroups = useStoreState(state => state.clientAllocation.locationGroups);
  const role = useRole();
  const { isGlobalUser, isActTeamMember } = role;

  const onGetMyClinic = useStoreActions(actions => actions.clinic.onGetMyClinic);
  const onGetClinicActTeam = useStoreActions(actions => actions.actTeam.onGetClinicActTeam);
  const onGetActTeamsAvailable = useStoreActions(actions => actions.actTeam.onGetActTeamsAvailable);
  const onGetAllocationClientsByGroup = useStoreActions(
    actions => actions.clientAllocation.onGetAllocationClientsByGroup,
  );
  const onGetAllocationClientsByGroupGU = useStoreActions(
    actions => actions.clientAllocation.onGetAllocationClientsByGroupGU,
  );

  const [teamMembers, setTeamMembers] = useState<ITeamMemberMapped[]>([]);
  const [clients, setClients] = useState<IOption[]>([]);
  const [allocationMode, setAllocationMode] = useState<AllocationMode>(AllocationMode.BY_CLIENT);

  const clinicId = userClinic?.id ? String(userClinic.id) : '';

  const { isLoading } = useApiRequest(
    async () => {
      await onGetMyClinic();

      if (isGlobalUser) {
        await onGetActTeamsAvailable({ clinicId });
        await onGetAllocationClientsByGroupGU({ clinicId });
        setTeamMembers([
          {
            id: userId,
            fullName: extractFullName(user),
            photo: photo ?? null,
            isDeleted: false,
          },
        ]);
        return;
      }

      await onGetClinicActTeam({ clinicId, actTeamId });

      const [clientsResponse, teamMembersResponse] = await Promise.all([
        api.clientAllocation.getRelatedClients(clinicId, actTeamId),
        api.actTeamMember.getActTeamMemberList(clinicId, actTeamId),
      ]);

      setClients(clientsResponse.data.map(clientMapper));

      const mapped = teamMembersResponse.data.sort(sortByName).map(teamMemberMapper);
      setTeamMembers(isActTeamMember ? mapped.filter(m => m.id === userId) : mapped);

      await onGetAllocationClientsByGroup({ clinicId, teamId: actTeamId });
    },
    {
      dependencies: [actTeamId, userClinic?.id, userId],
    },
  );

  if (!userClinic?.id) {
    return null;
  }

  const state = useMemo(
    () => ({
      isLoading,
      teamMembers: teamMembers.filter(m => !m.isDeleted),
      actTeamId,
      clinicId,
      clients,
      locationGroups,
      allocationMode,
      setAllocationMode,
      currentUserRole: role,
    }),
    [isLoading, teamMembers, actTeamId, clinicId, clients, locationGroups, allocationMode, role],
  );

  return (
    <AddClientAllocationContext.Provider value={state}>
      {children}
    </AddClientAllocationContext.Provider>
  );
};

export const useAddClientAllocationContext = (): AddClientAllocationContextValue => {
  const context = useContext(AddClientAllocationContext);

  if (!context) {
    throw new Error(
      'useAddClientAllocationContext must be used within AddClientAllocationProvider',
    );
  }

  return context;
};
